/**
 * Interface sounds.
 *
 * The cues that ship as audio files play from those files; the handful with no
 * file of their own stay synthesised with WebAudio, so nothing is silent and
 * the set still weighs almost nothing. Everything routes through one gain node,
 * which is what makes the volume slider apply to both kinds at once.
 *
 * Files are fetched and decoded once, on the first gesture that unlocks audio.
 * Decoding eight short clips takes a few milliseconds, and after that a cue is
 * a buffer-source away.
 */

type Shape = {
  type?: OscillatorType;
  gain?: number;
  /** Slide to this frequency across the note, for the mute/unmute swoops. */
  glide?: number;
};

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let enabled = true;
let volume = 0.5;

function audio(): AudioContext | null {
  if (!ctx) {
    try {
      ctx = new AudioContext();
      master = ctx.createGain();
      master.gain.value = volume;
      master.connect(ctx.destination);
    } catch {
      return null;
    }
  }
  if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
  return ctx;
}

/* ------------------------------------------------------------- samples */

/** Cue -> file in /sounds. Anything absent here falls back to a tone. */
const FILES = {
  message: 'message-notification-mention',
  mention: 'message-notification-mention',
  join: 'channel-join',
  leave: 'channel-leave',
  voiceOn: 'channel-join',
  voiceOff: 'channel-leave',
  mute: 'mute',
  unmute: 'unmute',
  deafen: 'deafen',
  undeafen: 'undeafen',
  shareStart: 'screenshare-start'
} as const;

const buffers = new Map<string, AudioBuffer>();
let loading: Promise<void> | null = null;

/** Decodes every distinct file once. Safe to call repeatedly. */
export function loadSounds(): Promise<void> {
  if (loading) return loading;
  const c = audio();
  if (!c) return Promise.resolve();

  const names = [...new Set(Object.values(FILES))];
  loading = Promise.all(
    names.map(async (name) => {
      try {
        const res = await fetch('./sounds/' + name + '.wav');
        if (!res.ok) return;
        buffers.set(name, await c.decodeAudioData(await res.arrayBuffer()));
      } catch {
        /* this cue falls back to its tone */
      }
    })
  ).then(() => {});
  return loading;
}

function playFile(name: string): boolean {
  const c = audio();
  const buf = buffers.get(name);
  if (!c || !master || !buf) return false;
  const src = c.createBufferSource();
  src.buffer = buf;
  src.connect(master);
  src.start();
  return true;
}

/* --------------------------------------------------------- synthesised */

/** One shaped tone. */
function tone(freq: number, start: number, duration: number, opts: Shape = {}) {
  const c = audio();
  if (!c || !master) return;

  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = opts.type ?? 'sine';
  const t0 = c.currentTime + start;

  osc.frequency.setValueAtTime(freq, t0);
  if (opts.glide) osc.frequency.exponentialRampToValueAtTime(opts.glide, t0 + duration);

  // Ramps run between non-zero values because exponentialRamp cannot touch
  // zero; 0.0001 is silent and keeps the curve smooth instead of clicking.
  const peak = opts.gain ?? 0.22;
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(peak, t0 + Math.min(0.02, duration / 3));
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

  osc.connect(gain);
  gain.connect(master);
  osc.start(t0);
  osc.stop(t0 + duration + 0.02);
}

const CUES = {
  message:    () => { tone(880, 0, 0.09, { type: 'triangle', gain: 0.16 }); },
  mention:    () => { tone(740, 0, 0.09, { type: 'triangle' }); tone(1108, 0.08, 0.12, { type: 'triangle' }); },
  join:       () => { tone(523, 0, 0.1, { type: 'sine' }); tone(784, 0.08, 0.16, { type: 'sine' }); },
  leave:      () => { tone(659, 0, 0.1, { type: 'sine' }); tone(440, 0.08, 0.18, { type: 'sine' }); },
  voiceOn:    () => { tone(392, 0, 0.08, { type: 'square', gain: 0.1 }); tone(587, 0.07, 0.1, { type: 'square', gain: 0.1 }); tone(784, 0.14, 0.16, { type: 'square', gain: 0.1 }); },
  voiceOff:   () => { tone(784, 0, 0.08, { type: 'square', gain: 0.1 }); tone(523, 0.07, 0.16, { type: 'square', gain: 0.1 }); },
  mute:       () => { tone(520, 0, 0.07, { type: 'sine', gain: 0.14, glide: 380 }); },
  unmute:     () => { tone(380, 0, 0.07, { type: 'sine', gain: 0.14, glide: 560 }); },
  deafen:     () => { tone(560, 0, 0.09, { type: 'sine', gain: 0.14, glide: 300 }); },
  undeafen:   () => { tone(300, 0, 0.09, { type: 'sine', gain: 0.14, glide: 600 }); },
  shareStart: () => { tone(587, 0, 0.08, { type: 'triangle', gain: 0.13 }); tone(880, 0.07, 0.14, { type: 'triangle', gain: 0.13 }); },
  invite:     () => { tone(659, 0, 0.1, { type: 'triangle' }); tone(880, 0.09, 0.1, { type: 'triangle' }); tone(1046, 0.18, 0.18, { type: 'triangle' }); },
  success:    () => { tone(587, 0, 0.09, { type: 'sine' }); tone(880, 0.08, 0.18, { type: 'sine' }); },
  error:      () => { tone(320, 0, 0.16, { type: 'sawtooth', gain: 0.13, glide: 190 }); },
  pop:        () => { tone(1200, 0, 0.05, { type: 'sine', gain: 0.09 }); },
  /*
   * The ring.
   *
   * Two pulses rather than two notes: a phone rings twice and pauses, and
   * that shape is what makes a sound read as somebody calling rather than as
   * a notification. Each pulse is a root with a fifth and an octave over it,
   * quiet and detuned by a few milliseconds, which is what gives it the warmth
   * of a struck bell instead of the flatness of a single oscillator.
   */
  ring:       () => {
    for (const at of [0, 0.44]) {
      tone(523, at,         0.34, { type: 'sine',     gain: 0.135 });
      tone(784, at + 0.006, 0.30, { type: 'sine',     gain: 0.085 });
      tone(1046, at + 0.012, 0.22, { type: 'triangle', gain: 0.042 });
    }
  },
  connect:    () => { tone(440, 0, 0.07, { type: 'sine', gain: 0.12 }); tone(660, 0.06, 0.12, { type: 'sine', gain: 0.12 }); },
  disconnect: () => { tone(660, 0, 0.07, { type: 'sine', gain: 0.12 }); tone(330, 0.06, 0.16, { type: 'sine', gain: 0.12 }); }
} satisfies Record<string, () => void>;

export type Cue = keyof typeof CUES;

export const SOUND_NAMES = Object.keys(CUES) as Cue[];

/** True when this cue plays a real recording rather than a tone. */
export function isSampled(name: Cue): boolean {
  const file = (FILES as Partial<Record<Cue, string>>)[name];
  return !!file && buffers.has(file);
}

function fire(name: Cue) {
  const file = (FILES as Partial<Record<Cue, string>>)[name];
  if (file && playFile(file)) return;
  // Not decoded yet (or no file at all) - the tone covers this hit and the
  // files will be ready for the next one.
  if (file) void loadSounds();
  try { CUES[name](); } catch { /* audio unavailable */ }
}

export function play(name: Cue) {
  if (!enabled) return;
  fire(name);
}

/** Always plays, ignoring the enabled flag - used by the Test buttons. */
export function preview(name: Cue) {
  fire(name);
}

export function configure(opts: { enabled?: boolean; volume?: number }) {
  if (opts.enabled !== undefined) enabled = opts.enabled;
  if (opts.volume !== undefined) {
    volume = Math.max(0, Math.min(1, opts.volume));
    if (master) master.gain.value = volume;
  }
}

export function soundState() { return { enabled, volume }; }
